// @flow

import React, {useEffect, useState} from 'react';
import {useParams, useHistory} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Hidden,
  Radio,
  RadioGroup,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {InfoOutlined} from '@material-ui/icons';
import {colors} from '../../../../../assets/styles/colors';

import IosSwitch from '../../../../../elements/IosSwitch/IosSwitch';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import CustomTextField from '../../../../../elements/CustomTextField/CustomTextField';

import {ALLOWED_CHECKOUT_INPUTS} from '../../constants/allowed-inputs';

import CalendarForm from '../InputForm/CalendarForm';
import InputPreview from '../Modals/InputPreview';
import MultiOptionForm from '../InputForm/MultiOptionForm';

import textPreview from '../../../../../assets/img/configure/text-input-preview.png';
import calendarPreview from '../../../../../assets/img/configure/calendar-input-preview.png';
import multiOptionPreview from '../../../../../assets/img/configure/select-input-preview.png';

import {useBindedActions} from '../../../../../utils/globalStore';
import {actions as CheckoutActions} from '../../duck';
import {useTypedSelector} from '../../../../../store';
import LoadingSpinner from '../../../../../elements/LoadingSpinner/LoadingSpinner';

import SnackBar from '../../../../../elements/SnackBar/SnackBar';
import {allowedDaysToValues} from '../../utils';
import {useIsFeatureInTier} from '../../../../../utils/component';

import {INPUT_TYPE, type InputType} from '../../../../../entities';
import type {Theme} from '@material-ui/core';
import {type CheckoutAIRequest} from '../../api';

type Props = {};

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    padding: theme.spacing(1, 2, 2, 2),
    border: `1px solid ${colors.grey300}`,
    borderRadius: 12,
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  text: {
    fontWeight: 500,
    fontSize: 14,
    color: colors.grey900,
  },
  greyText: {
    fontWeight: 400,
    fontSize: 14,
    color: colors.grey700,
  },
  textDisabled: {
    fontWeight: 400,
    fontSize: 14,
    color: colors.grey700,
  },
  divider: {
    margin: theme.spacing(1, 0),
  },
  semiBold: {
    fontWeight: 600,
  },
  saveButton: {
    width: '10rem',
    padding: theme.spacing(1, 2),
  },
  form: {
    margin: theme.spacing(1.5, 0),
  },
  wrapper: {
    padding: theme.spacing(1.5),
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    backgroundColor: 'white',
    borderRadius: '12px',
    border: `1px solid ${colors.grey300}`,
  },
  rowOptional: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  radioRow: {
    display: 'flex',
    alignItems: 'center',
  },
  iconInfo: {
    color: colors.grey600,
  },
  requiredCheckboxWrap: {
    width: 'fit-content',
  },
}));

const actions = {
  createCheckoutInput: CheckoutActions.createCheckoutInput,
  editCheckoutInput: CheckoutActions.editCheckoutInput,
  getCheckoutInput: CheckoutActions.fetchCheckoutInput,
};

export default function CheckoutInput(props: Props) {
  const classes = useStyles();
  const history = useHistory();
  const {id} = useParams();

  const {
    register,
    errors,
    handleSubmit,
    setValue,
    control,
    getValues,
  } = useForm({
    mode: 'onBlur',
  });

  const {
    createCheckoutInput,
    editCheckoutInput,
    getCheckoutInput,
  } = useBindedActions(actions);

  const hasCustomCheckoutFeature = useIsFeatureInTier('CUSTOM_ORDER_CHECKOUT');

  const saveError = useTypedSelector(
    state => state.salesProcessConfig.saveInputError
  );
  const isSaving = useTypedSelector(state => state.salesProcessConfig.isSaving);
  const isFetching = useTypedSelector(
    state => state.salesProcessConfig.isFetchingInput
  );
  const fetchInputError = useTypedSelector(
    state => state.salesProcessConfig.fetchInputError
  );

  const checkoutInput = useTypedSelector(
    state => state.salesProcessConfig.checkoutInput
  );

  const [enabled, setEnabled] = useState<boolean>(false);
  const [required, setRequired] = useState<boolean>(false);
  const [modalType, setModalType] = useState<string | null>(null);
  const [inputType, setInputType] = useState<InputType>(INPUT_TYPE.TEXT);

  // multi-options
  const [options, setOptions] = useState<string[]>([]);

  //calendar
  const [enabledMinDays, setEnabledMinDays] = useState<boolean>(false);
  const [availableDays, setAvailableDays] = useState<string[]>([]);

  const onInputTypeChange = e => {
    setInputType(e.target.value);
  };

  const handleCloseModal = () => setModalType(null);

  const handleEnableInput = e => {
    setEnabled(e.target.checked);
  };

  const onRequiredChange = e => setRequired(e.target.checked);

  const buildCheckoutInput = () => {
    switch (inputType) {
      case INPUT_TYPE.DATE_PICKER:
        return (
          <CalendarForm
            checkoutInput={checkoutInput}
            register={register}
            errors={errors}
            availableDays={availableDays}
            setAvailableDays={setAvailableDays}
            enabledMinDays={enabledMinDays}
            setEnabledMinDays={setEnabledMinDays}
            setValue={setValue}
            getValues={getValues}
          />
        );
      case INPUT_TYPE.CHECKLIST:
        return (
          <MultiOptionForm
            checkoutInput={checkoutInput}
            control={control}
            setValue={setValue}
            register={register}
            errors={errors}
            options={options}
            setOptions={setOptions}
          />
        );
      default:
        return null;
    }
  };

  const buildModalPreview = () => {
    switch (modalType) {
      case INPUT_TYPE.TEXT:
        return (
          <InputPreview
            handleClose={handleCloseModal}
            title="Texto"
            img={textPreview}
          />
        );
      case INPUT_TYPE.NUMBER:
        return (
          <InputPreview
            handleClose={handleCloseModal}
            title="Número"
            img={textPreview}
          />
        );
      case INPUT_TYPE.DATE_PICKER:
        return (
          <InputPreview
            handleClose={handleCloseModal}
            title="Calendario"
            img={calendarPreview}
          />
        );
      case INPUT_TYPE.CHECKLIST:
        return (
          <InputPreview
            handleClose={handleCloseModal}
            title="Multi Opción"
            img={multiOptionPreview}
          />
        );
      default:
        return null;
    }
  };

  const onSubmitData = data => {
    const {name, description, minOptions, maxOptions, minDays} = data;
    const checkoutInputRequest: CheckoutAIRequest = {
      enabled,
      required,
      name,
      description,
      // $FlowFixMe
      inputType,
    };

    if (inputType === INPUT_TYPE.CHECKLIST) {
      checkoutInputRequest.checklistConfig = {
        options,
        minSelectedOptions: minOptions ? Number(minOptions) : undefined,
        maxSelectedOptions: maxOptions ? Number(maxOptions) : undefined,
      };
    }

    if (inputType === INPUT_TYPE.DATE_PICKER) {
      const parsedDays = availableDays.map(el => el.substring(0, 3));
      checkoutInputRequest.datePickerConfig = {
        allowedWeekdays: parsedDays,
        enabledMinDaysToSelect: enabledMinDays,
        minDaysToSelect: minDays ? Number(minDays) : undefined,
      };
    }

    if (checkoutInput && id) {
      editCheckoutInput({
        checkoutInputRequest: {
          id,
          ...checkoutInputRequest,
        },
      });
    } else {
      createCheckoutInput({checkoutInputRequest});
    }
  };

  useEffect(() => {
    if (!hasCustomCheckoutFeature) history.push('/configure/sales-process');
  }, [hasCustomCheckoutFeature, history]);

  useEffect(() => {
    if (id && id !== 'new') {
      getCheckoutInput({checkoutInputRequest: {id}});
    } else if (id === 'new') {
      setEnabled(true);
    }
  }, [getCheckoutInput, id]);

  useEffect(() => {
    if (checkoutInput) {
      setEnabled(checkoutInput.enabled);
      setRequired(checkoutInput.required);
      setInputType(checkoutInput.inputType);

      if (!checkoutInput.schema) return;

      const schema = checkoutInput.schema;

      if (checkoutInput.inputType === INPUT_TYPE.CHECKLIST) {
        setOptions(schema.value ?? []);
      }

      if (checkoutInput.inputType === INPUT_TYPE.DATE_PICKER) {
        setEnabledMinDays(!!schema.enabled_min_days_to_select);
        setAvailableDays(allowedDaysToValues(schema.allowed_weekdays ?? []));
      }
    }
  }, [checkoutInput, register, setValue]);

  return (
    <div className={classes.card}>
      {(isSaving || isFetching) && <LoadingSpinner />}
      {!!fetchInputError && <SnackBar mode="error" message={fetchInputError} />}
      {!!saveError && <SnackBar mode="error" message={saveError} />}
      {buildModalPreview()}
      {!isFetching && (id === 'new' || checkoutInput) && (
        <div className={classes.cardContent}>
          <div className={classes.cardHeader}>
            <Typography className={`${classes.text} ${classes.semiBold}`}>
              Campo 1
            </Typography>
            <FormControlLabel
              control={
                <IosSwitch
                  checked={enabled}
                  name="enabled"
                  onChange={handleEnableInput}
                />
              }
              labelPlacement="start"
              label={
                <Typography className={classes.text}>
                  {enabled ? 'Activado' : 'Desactivado'}
                </Typography>
              }
            />
          </div>
          <Divider className={classes.divider} />
          <div>
            <Typography className={classes.text}>
              Selecciona el tipo de campo
            </Typography>

            <div>
              <RadioGroup
                name="inputType"
                value={inputType}
                onChange={onInputTypeChange}
              >
                <Grid container>
                  {ALLOWED_CHECKOUT_INPUTS.map(elem => (
                    <Grid
                      item
                      xs={6}
                      md={3}
                      className={classes.radioRow}
                      key={elem.name}
                    >
                      <FormControlLabel
                        value={elem.inputType}
                        control={<Radio color="primary" />}
                        label={
                          <Typography
                            className={
                              inputType === elem.inputType
                                ? classes.text
                                : classes.textDisabled
                            }
                          >
                            {elem.name}
                          </Typography>
                        }
                      />
                      <InfoOutlined
                        onClick={() => setModalType(elem.inputType)}
                        className={classes.iconInfo}
                      />
                    </Grid>
                  ))}
                  <Hidden smDown>
                    <Grid item md={2} />
                  </Hidden>
                </Grid>
              </RadioGroup>
            </div>

            <div className={classes.form}>
              <div className={classes.wrapper}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <div className={classes.wrap}>
                          <Grid container spacing={1}>
                            <Grid item xs={12}>
                              <Typography className={classes.text}>
                                Nombre del campo/ Instrucciones
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <CustomTextField
                                name="name"
                                error={!!errors.name}
                                defaultValue={
                                  checkoutInput && checkoutInput.name
                                }
                                helperText={
                                  errors.name ? errors.name.message : ''
                                }
                                inputRef={register({
                                  required: 'Campo obligatorio',
                                  maxLength: {
                                    value: 100,
                                    message: 'Máximo 100 caracteres',
                                  },
                                })}
                                type="text"
                                // disabled={isSaving}
                              />
                            </Grid>
                          </Grid>
                        </div>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <div className={classes.wrap}>
                          <Grid container spacing={1}>
                            <Grid item xs={12}>
                              <div className={classes.rowOptional}>
                                <Typography className={classes.text}>
                                  Texto Adicional
                                </Typography>
                                <Typography className={classes.greyText}>
                                  (Opcional)
                                </Typography>
                              </div>
                            </Grid>
                            <Grid item xs={12}>
                              <CustomTextField
                                name="description"
                                error={!!errors.description}
                                defaultValue={
                                  checkoutInput && checkoutInput.description
                                }
                                helperText={
                                  errors.description
                                    ? errors.description.message
                                    : ''
                                }
                                inputRef={register({
                                  maxLength: {
                                    value: 200,
                                    message: 'Máximo 200 caracteres',
                                  },
                                })}
                                type="text"
                              />
                            </Grid>
                          </Grid>
                        </div>
                      </Grid>
                    </Grid>
                  </Grid>
                  {buildCheckoutInput()}
                </Grid>
              </div>
            </div>
          </div>
          <div className={classes.requiredCheckboxWrap}>
            <FormControlLabel
              classes={{label: classes.checkboxLabel}}
              control={
                <Checkbox
                  checked={required}
                  onChange={onRequiredChange}
                  color="primary"
                  // disabled={isSaving}
                />
              }
              label={
                <Typography className={classes.text}>
                  Campo obligatorio
                </Typography>
              }
            />
          </div>
          <CustomButton
            variant="contained"
            className={classes.saveButton}
            onClick={handleSubmit(onSubmitData)}
          >
            Guardar
          </CustomButton>
        </div>
      )}
    </div>
  );
}

CheckoutInput.propTypes = {};
